import { Placement, PositioningStrategy, VirtualElement } from "@popperjs/core";
import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { usePopper } from "react-popper";
import './Tooltip.scss';

export interface ITooltipProps {
    children?: ReactNode,
    placement?: Placement,
    referenceElement: Element | VirtualElement,
    show?: boolean,
    showOnElement?: Element,
    showOnElementDelay?: number, // In milliseconds
    showOnElementEvents?: Array<'hover' | 'focus' | 'click'>,
    strategy?: PositioningStrategy,
}

export function Tooltip({
    children,
    placement = 'top',
    referenceElement,
    show: showProp,
    showOnElement,
    showOnElementDelay = 0,
    showOnElementEvents,
    strategy = 'absolute'
}: ITooltipProps) {
    const [popEl, setPopEl] = useState(null);
    const [popArrowEl, setPopArrowEl] = useState(null);
    const pop = usePopper(referenceElement, popEl, {
        placement: placement,
        modifiers: [{
            name: 'arrow',
            options: {
                element: popArrowEl,
            },
        }, {
            name: 'offset',
            options: {
                offset: [0, 7],
            }
        }],
        strategy: strategy,
    });

    if (showProp !== undefined && showOnElementEvents) {
        console.warn(`Can't use "show" and "showOnElement" together, must choose one or the other.`)
    }

    const [showInternal, setShowInternal] = useState(() => {
        // If we're only showing on certain events, we want to start hidden.
        if (showOnElementEvents) return false;
        return true;
    });
    const isShowControlled = (showProp !== undefined);
    const show = isShowControlled ? showProp : showInternal;
    // We'll use a timeout so that we can have a delay before opening.
    // This is less annoying so that tooltips don't open immediately if the user mouses over them.
    const showTimeoutRef = useRef(undefined);
    // Cleanup the timeout
    useEffect(() => {
        return () => clearTimeout(showTimeoutRef.current)
    }, [])

    const onOpenCallback = useCallback(() => {
        clearTimeout(showTimeoutRef.current);

        showTimeoutRef.current = setTimeout(() => {
            setShowInternal(true)
        }, showOnElementDelay);
    }, []);
    
    const onCloseCallback = useCallback(() => {
        clearTimeout(showTimeoutRef.current);
        setShowInternal(false)
    }, []);

    // Separate callbacks for clicks because we want to prevent the default click action and we need to toggle
    const onClickToggleCallback = useCallback((e: MouseEvent) => {
        if (e.defaultPrevented) return;
        e.preventDefault();
        
        clearTimeout(showTimeoutRef.current);
        setShowInternal((val) => !val);
    }, []);

    if (!isShowControlled) {
        useEffect(() => {
            if (showOnElement && showOnElementEvents) {
                const eventsAndCallbacks: {event: string, callback: (e?: any) => void}[] = []

                for (const e of showOnElementEvents) {
                    switch (e) {
                        case 'hover':
                            eventsAndCallbacks.push({event: 'mouseenter', callback: onOpenCallback});
                            eventsAndCallbacks.push({event: 'mouseleave', callback: onCloseCallback});
                            break;
                        case 'focus':
                            eventsAndCallbacks.push({event: 'focus', callback: onOpenCallback});
                            eventsAndCallbacks.push({event: 'blur', callback: onCloseCallback});
                            break;
                        case 'click':
                            eventsAndCallbacks.push({event: 'click', callback: onClickToggleCallback});
                            break;
                    }
                }

                for (const {event, callback} of eventsAndCallbacks) {
                    showOnElement.addEventListener(event, callback);
                }

                return () => {
                    for (const { event, callback } of eventsAndCallbacks) {
                        showOnElement.removeEventListener(event, callback);
                    }   
                }
            }
        }, [showOnElement, showOnElementEvents, onOpenCallback, onCloseCallback]);
    }

    return <>
        {show && <div
            className="tooltip_wrap"
            style={pop.styles.popper}
            ref={setPopEl}
            {...pop.attributes.popper}
        >
            <div className="tooltip">
                {children}
                <div ref={setPopArrowEl} style={pop.styles.arrow} className="tooltip_arrow" />
            </div>
        </div>}
    </>
}